import { Input } from '@/components/ui/shadcn/input';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Check, Search } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

export interface SheetPickerOption {
    id: string;
    /** The primary line, and what the search matches on. */
    label: string;
    /** Secondary line — an email, a phone, an owning company. Also searched. */
    description?: ReactNode;
    /** Extra searchable text that is not displayed (e.g. a code). */
    keywords?: string;
    /** Trailing marker — a role, "Current", a status. */
    badge?: ReactNode;
    disabled?: boolean;
    /**
     * Heading this option sits under. Options are rendered in the order given,
     * so group them yourself — a heading is emitted whenever the value changes.
     */
    group?: string;
}

interface SheetPickerListBase {
    options: SheetPickerOption[];
    /** Hidden when there is nothing to filter — a handful of templates, say. */
    searchable?: boolean;
    /**
     * Pass both to drive the search yourself — for a list that is fetched per
     * query. `options` is then taken as already filtered and nothing is matched
     * locally. Omit both and the component filters what it was given.
     */
    search?: string;
    onSearchChange?: (search: string) => void;
    /** Replaces the rows with a loading line, for a server-backed search. */
    loading?: boolean;
    labels?: {
        search?: string;
        empty?: string;
        loading?: string;
    };
    className?: string;
}

interface SingleProps extends SheetPickerListBase {
    multiple?: false;
    value: string | null;
    onValueChange: (value: string) => void;
}

interface MultiProps extends SheetPickerListBase {
    multiple: true;
    values: string[];
    onValuesChange: (values: string[]) => void;
}

type SheetPickerListProps = SingleProps | MultiProps;

/**
 * The list-in-a-sheet picker: a search field on the top line and every option
 * laid out below it, rather than a dropdown inside an overlay.
 *
 * A sheet has the height to show the options outright, and a dropdown opening
 * over a sheet is a second layer for no benefit. The search sticks to the top
 * while the rows scroll, so this stays inside `ResponsiveModalBody` and the body
 * remains the one scroll container.
 */
export function SheetPickerList(props: SheetPickerListProps) {
    const { options, searchable = true, search: searchProp, onSearchChange, loading = false, labels, className } = props;
    const { t } = useTranslations();
    const [internalSearch, setInternalSearch] = useState('');

    const controlledSearch = searchProp !== undefined && onSearchChange !== undefined;
    const search = controlledSearch ? searchProp : internalSearch;
    const setSearch = controlledSearch ? onSearchChange : setInternalSearch;

    const filtered = useMemo(() => {
        // A controlled search means the caller already narrowed the list.
        const term = controlledSearch ? '' : search.trim().toLowerCase();
        if (term.length === 0) {
            return options;
        }

        return options.filter((option) => {
            const haystack = [option.label, typeof option.description === 'string' ? option.description : '', option.keywords ?? '']
                .join(' ')
                .toLowerCase();

            return haystack.includes(term);
        });
    }, [options, search, controlledSearch]);

    const isSelected = (id: string) => (props.multiple ? props.values.includes(id) : props.value === id);

    const toggle = (id: string) => {
        if (props.multiple) {
            props.onValuesChange(props.values.includes(id) ? props.values.filter((value) => value !== id) : [...props.values, id]);
            return;
        }

        props.onValueChange(id);
    };

    return (
        <div className={cn('flex flex-col gap-3', className)}>
            {searchable && (
                // Sticky rather than in its own pane: the modal body stays the
                // only scroll container while the field stays reachable.
                //
                // The negative margins bleed the strip out to the body's padding
                // edges and up to the very top of the scroll container, so rows
                // pass behind it instead of showing through the gap around it.
                <div className="bg-background sticky top-0 z-10 -mx-4 -mt-4 mb-1 px-4 pt-4 pb-2 md:-mx-6 md:px-6">
                    <div className="relative">
                        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                        <Input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={labels?.search ?? t('common.picker.search', 'Search…')}
                            className="pl-9"
                        />
                    </div>
                </div>
            )}

            {loading ? (
                <p className="text-muted-foreground py-6 text-center text-sm">{labels?.loading ?? t('common.picker.loading', 'Loading…')}</p>
            ) : filtered.length === 0 ? (
                <p className="text-muted-foreground py-6 text-center text-sm">{labels?.empty ?? t('common.picker.empty', 'Nothing found.')}</p>
            ) : (
                <ul className="space-y-1">
                    {filtered.map((option, index) => {
                        const selected = isSelected(option.id);
                        // The list is pre-grouped by the caller, so a heading is due
                        // wherever the group changes from the row before.
                        const startsGroup = option.group !== undefined && option.group !== filtered[index - 1]?.group;

                        return (
                            <li key={option.id}>
                                {startsGroup ? (
                                    <p className="text-muted-foreground px-3 pt-3 pb-1 text-xs font-medium tracking-wide uppercase">{option.group}</p>
                                ) : null}
                                <button
                                    type="button"
                                    disabled={option.disabled}
                                    onClick={() => toggle(option.id)}
                                    aria-pressed={selected}
                                    className={cn(
                                        'flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
                                        'disabled:pointer-events-none disabled:opacity-50',
                                        selected ? 'border-primary/40 bg-primary/10' : 'hover:bg-accent border-transparent',
                                    )}
                                >
                                    <Check className={cn('h-4 w-4 shrink-0', selected ? 'text-primary' : 'opacity-0')} />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-medium">{option.label}</span>
                                        {option.description ? (
                                            <span className="text-muted-foreground block truncate text-xs">{option.description}</span>
                                        ) : null}
                                    </span>
                                    {option.badge ? <span className="shrink-0">{option.badge}</span> : null}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
